import { parseLiveDimensionInches, parseLiveWeightPounds } from 'controllers/tractor_trailer_render_controller'

const SUPPORT_FIELDS = ['decision', 'length', 'offset', 'cargo_length_in']

export default class MatrixSupportEditor {
  constructor(controller) {
    this.controller = controller
    this.element = controller.supportEditorTarget
    this.drafts = new Map()
    this.handleInput = this.handleInput.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.element.addEventListener('input', this.handleInput)
    this.element.addEventListener('change', this.handleInput)
    this.element.addEventListener('click', this.handleClick)
  }

  disconnect() {
    this.element.removeEventListener('input', this.handleInput)
    this.element.removeEventListener('change', this.handleInput)
    this.element.removeEventListener('click', this.handleClick)
    if (this.previewFrame) window.cancelAnimationFrame(this.previewFrame)
  }

  field(name) { return this.element.querySelector(`[data-support-field="${name}"]`) }
  part(name) { return this.element.querySelector(`[data-support-part="${name}"]`) }
  shipments(profile) { return Array.isArray(profile.shipments) ? profile.shipments : [profile.shipment || {}] }
  entryKey(profile, index) { return `${profile.key}:${index}` }
  savedReview(profile, index) { return (this.controller.loadProfileOverrides[String(profile.key)] || {}).support_reviews?.[String(index)] }

  shipment(profile, index) {
    const source = this.shipments(profile)[index] || {}
    const override = this.controller.loadProfileOverrides[String(profile.key)] || {}
    const row = { ...source, ...override }
    row.commodity_description = override.label || source.commodity_description || profile.label
    ;['length', 'width', 'height'].forEach(dimension => {
      const field = `commodity_${dimension}`
      row[field] = Object.prototype.hasOwnProperty.call(override, field)
        ? override[field] : source[field] || source[`${field}_raw`] || (source[`${field}_in`] == null ? '' : `${source[`${field}_in`]} in`)
      if (Object.prototype.hasOwnProperty.call(override, field)) delete row[`${field}_in`]
    })
    row.commodity_weight = Object.prototype.hasOwnProperty.call(override, 'commodity_weight')
      ? override.commodity_weight : source.commodity_weight_lb != null ? `${Number(source.commodity_weight_lb).toLocaleString(undefined, { maximumFractionDigits: 0 })} lb` : source.commodity_weight || source.commodity_weight_raw || ''
    if (Object.prototype.hasOwnProperty.call(override, 'commodity_weight')) {
      delete row.commodity_weight_lb
      delete row.weight_lb
    }
    return row
  }

  cargoLength(profile, index) { return parseLiveDimensionInches(this.shipment(profile, index).commodity_length, 'commodity_length') }

  required(profile, index) {
    const row = this.shipment(profile, index)
    const hints = this.controller.plan.support_review_hints?.[String(profile.key)]?.[index] || {}
    const length = this.cargoLength(profile, index)
    const weight = parseLiveWeightPounds(row.commodity_weight)
    const labelChanged = this.controller.loadProfileOverrides[String(profile.key)]?.label
    return Boolean(hints.description_requires_support || labelChanged || row.commodity_support_length || row.commodity_support_length_raw || row.commodity_support_offset || row.commodity_support_offset_raw ||
      (length > 636) || (length >= 240 && weight >= 50000) || (length >= 312 && weight >= 40000))
  }

  initialReview(profile, index) {
    const saved = this.savedReview(profile, index)
    if (saved) return { ...saved, offset: saved.offset == null ? '' : String(saved.offset) }
    const row = this.shipment(profile, index)
    const support = row.commodity_support_length || row.commodity_support_length_raw || ''
    const offset = row.commodity_support_offset == null || row.commodity_support_offset === '' ? row.commodity_support_offset_raw : row.commodity_support_offset
    return {
      decision: support || this.required(profile, index) ? 'custom' : '',
      length: support,
      offset: offset == null ? '' : String(offset),
      cargo_length_in: this.cargoLength(profile, index),
    }
  }

  errorFor(profile, index, review, requireDecision = true) {
    const overall = this.cargoLength(profile, index)
    if (!review?.decision) return requireDecision && this.required(profile, index) ? 'Choose a support setup for this component.' : null
    if (!(overall > 0)) return 'Enter a valid cargo length before reviewing support.'
    if (Math.abs(Number(review.cargo_length_in) - overall) >= 0.01 || review.cargo_length_in == null) return 'Cargo length changed. Review and save the support setup again.'
    if (['full_length', 'unknown'].includes(review.decision)) return null
    if (review.decision !== 'custom') return 'Choose a valid support setup.'
    const support = parseLiveDimensionInches(review.length, 'commodity_length')
    if (!(support > 0)) return 'Enter a valid positive support length.'
    if (support > overall) return 'Support length cannot exceed cargo length.'
    const offset = String(review.offset || '').trim() ? parseLiveDimensionInches(review.offset, 'commodity_support_offset', { allowZero: true }) : (overall - support) / 2
    if (offset == null || offset < 0 || offset + support > overall + 0.001) return 'Offset must keep the support base inside the cargo length.'
    return null
  }

  laneCount(profile) {
    let count = 0
    this.controller.selectedLaneKeys.forEach(key => {
      if (Array.isArray(profile.lane_keys) && !profile.lane_keys.includes(key)) return
      const excluded = Array.from(this.controller.excludedCellPairs.values()).some(pair => pair.lane_key === key && pair.load_profile_key === String(profile.key))
      if (!excluded) count += 1
    })
    return count
  }

  pending() {
    const pending = []
    this.controller.loadProfiles.forEach(profile => {
      if (!this.controller.selectedLoadProfileKeys.has(String(profile.key)) || this.laneCount(profile) === 0) return
      this.shipments(profile).forEach((_shipment, index) => {
        if (this.drafts.has(this.entryKey(profile, index)) || this.errorFor(profile, index, this.initialReview(profile, index))) pending.push({ profile, index })
      })
    })
    return pending
  }

  status(profile) {
    const reviews = this.shipments(profile).map((_shipment, index) => this.initialReview(profile, index))
    if (reviews.some((review, index) => this.drafts.has(this.entryKey(profile, index)) || this.errorFor(profile, index, review))) return 'Review needed'
    if (reviews.some(review => !review.decision || review.decision === 'unknown')) return 'Full length assumed'
    if (reviews.length > 1) return 'Components reviewed'
    const review = reviews[0]
    if (review.decision === 'full_length') return 'Full length confirmed'
    if (review.decision === 'custom') return `${review.length} · ${review.offset ? `${review.offset} offset` : 'centered'}`
    return 'Full length assumed'
  }

  createCell(profile) {
    const cell = this.controller.elementFor('td', 'lane-matrix-review__support-cell')
    const status = this.status(profile)
    const label = this.controller.elementFor('div', status === 'Review needed' ? 'text-warning' : '', status)
    label.dataset.supportStatus = String(profile.key)
    cell.appendChild(label)
    const button = this.controller.elementFor('button', 'btn btn-link', 'Review support')
    button.type = 'button'
    button.setAttribute('aria-label', `Review support for ${profile.label || profile.key}`)
    button.addEventListener('click', () => this.open(profile, this.pending().find(entry => entry.profile.key === profile.key)?.index || 0, true))
    cell.appendChild(button)
    const trailer = this.controller.profileReviewFieldValue(profile, 'trailer_preference_raw')
    if (trailer) cell.appendChild(this.controller.elementFor('small', 'text-muted', `Transport: ${trailer}`))
    return cell
  }

  update() {
    const pending = this.pending()
    const profiles = [...new Map(pending.map(entry => [entry.profile.key, entry.profile])).values()]
    const loadLabel = profiles.length === 1 ? 'load type' : 'load types'
    this.controller.supportReadinessTarget.classList.toggle('text-warning', profiles.length > 0)
    this.controller.supportReadinessTarget.classList.toggle('text-success', profiles.length === 0)
    this.controller.supportReadinessTarget.textContent = profiles.length
      ? `${profiles.length} ${loadLabel} ${profiles.length === 1 ? 'needs' : 'need'} support review · ${profiles.reduce((sum, profile) => sum + this.laneCount(profile), 0).toLocaleString()} combinations affected. Review support before ${this.controller.projectEditValue ? 'finishing' : 'creating the project'}.`
      : 'Support review complete. Full-length assumptions stay visible in the project.'
    this.controller.profileRowsTarget.querySelectorAll('[data-support-status]').forEach(label => {
      const profile = this.controller.profilesByKey.get(label.dataset.supportStatus)
      label.textContent = this.status(profile)
      label.classList.toggle('text-warning', label.textContent === 'Review needed')
    })
    if (this.profile) this.part('scope').textContent = `Applies to ${this.laneCount(this.profile).toLocaleString()} ${this.laneCount(this.profile) === 1 ? 'lane' : 'lanes'}`
  }

  focusPending() {
    const next = this.pending()[0]
    if (!next) return false
    this.open(next.profile, next.index, true)
    return true
  }

  open(profile, index = 0, focus = false) {
    this.profile = profile
    this.index = index
    this.element.hidden = false
    this.review = { ...(this.drafts.get(this.entryKey(profile, index)) || this.initialReview(profile, index)), cargo_length_in: this.cargoLength(profile, index) }
    this.part('title').textContent = this.controller.effectiveProfileLabel(profile)
    const shipments = this.shipments(profile)
    this.field('component').replaceChildren(...shipments.map((shipment, position) => {
      const option = document.createElement('option')
      option.value = position
      option.textContent = `${position + 1}. ${shipment.commodity_description || 'Component'}`
      return option
    }))
    this.field('component').value = String(index)
    this.part('components').hidden = shipments.length <= 1
    this.field('decision').value = this.review.decision
    this.field('length').value = this.review.length || ''
    this.field('offset').value = this.review.offset || ''
    this.field('offset_mode').value = this.review.offset ? 'manual' : 'auto'
    this.field('trailer').value = this.controller.profileReviewFieldValue(profile, 'trailer_preference_raw')
    this.field('trailer').disabled = this.controller.projectEditValue || this.controller.processingValue || this.controller.supportSaving
    this.part('error').hidden = true
    this.renderDraft()
    this.update()
    let activeRow
    this.controller.profileRowsTarget.querySelectorAll('tr[data-profile-key]').forEach(row => {
      const active = row.dataset.profileKey === String(profile.key)
      row.classList.toggle('is-active-profile', active)
      if (active) activeRow = row
    })
    if (focus) {
      activeRow?.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      this.element.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      this.field(this.review.decision === 'custom' ? 'length' : 'decision').focus({ preventScroll: true })
    }
  }

  handleInput(event) {
    const name = event.target.dataset.supportField
    if (!name || this.controller.supportSaving || this.controller.processingValue) return
    if (name === 'component') return this.open(this.profile, Number(event.target.value))
    if (name === 'trailer') {
      this.controller.setProfileOverride(String(this.profile.key), 'trailer_preference_raw', event.target.value, this.controller.profileReviewFieldValue(this.profile, 'trailer_preference_raw', { includeOverride: false }))
    } else if (name === 'offset_mode') {
      this.review.offset = event.target.value === 'auto' ? '' : this.field('offset').value
    } else {
      this.review[name] = event.target.value
    }
    this.review.cargo_length_in = this.cargoLength(this.profile, this.index)
    this.drafts.set(this.entryKey(this.profile, this.index), { ...this.review })
    this.part('error').hidden = true
    this.renderDraft()
    this.update()
  }

  handleClick(event) {
    if (event.target.closest('[data-save-support]')) this.save()
  }

  renderDraft() {
    const row = this.shipment(this.profile, this.index)
    const custom = this.review.decision === 'custom'
    this.part('custom').hidden = !custom
    this.part('offset').hidden = !custom || this.field('offset_mode').value === 'auto'
    this.part('assumption').hidden = this.review.decision !== 'unknown'
    this.part('dimensions').textContent = `${row.commodity_length} × ${row.commodity_width} × ${row.commodity_height} · ${row.commodity_weight}`
    row.commodity_support_length = custom ? this.review.length : this.review.decision === 'full_length' ? row.commodity_length : ''
    row.commodity_support_offset = custom ? this.review.offset : this.review.decision === 'full_length' ? '0 in' : ''
    row.commodity_support_offset_mode = this.review.offset ? 'manual' : 'auto'
    delete row.commodity_support_length_in
    delete row.commodity_support_offset_in
    delete row.support_length_in
    delete row.support_offset_in
    const error = this.errorFor(this.profile, this.index, this.review, false)
    if (error) {
      row.commodity_support_length = ''
      row.commodity_support_offset = ''
    }
    this.part('preview-note').textContent = error || (this.review.decision === 'unknown' ? 'Full-length support assumed.' : custom && !this.review.offset ? 'Support base centered automatically.' : '')
    if (this.previewFrame) window.cancelAnimationFrame(this.previewFrame)
    this.previewFrame = window.requestAnimationFrame(() => {
      this.previewFrame = null
      this.element.querySelector('[data-behavior="quote-v2-load-preview"]').dispatchEvent(new CustomEvent('load-preview:replace', { detail: { rows: [row] } }))
    })
  }

  profileChanged(profile) {
    if (this.profile?.key === profile.key) {
      this.review.cargo_length_in = this.cargoLength(profile, this.index)
      this.renderDraft()
    }
    this.update()
  }

  async save() {
    if (this.controller.processingValue || this.controller.supportSaving) return
    const missingOffset = this.review.decision === 'custom' && this.field('offset_mode').value === 'manual' && !String(this.review.offset || '').trim()
    const error = missingOffset ? 'Enter an offset or choose centered automatically.' : this.errorFor(this.profile, this.index, this.review)
    if (error) {
      this.part('error').textContent = error
      this.part('error').hidden = false
      return
    }
    const profile = this.profile, index = this.index
    const key = String(profile.key)
    const override = this.controller.loadProfileOverrides[key] ||= {}
    const previous = this.controller.deepClone(override.support_reviews || {})
    if (this.review.decision) {
      override.support_reviews ||= {}
      override.support_reviews[String(index)] = Object.fromEntries(SUPPORT_FIELDS.map(field => [field, this.review[field] ?? '']))
    }
    try {
      await this.controller.saveSetup()
      this.drafts.delete(this.entryKey(profile, index))
      this.controller.renderProfiles()
      this.controller.showStatus(`Saved ${profile.label || key} support setup for ${this.laneCount(profile)} lanes.`, 'success')
      if (!this.focusPending()) this.open(profile, index)
    } catch (error) {
      override.support_reviews = previous
      this.part('error').textContent = error.message
      this.part('error').hidden = false
    }
    this.update()
  }
}
